import "@cyber-range/cyber-range-lib-ui/dist/style.css"
import { TrackJS } from 'trackjs';
import createMessages from '@entities/strings/strings';
import { App, createApp } from 'vue';
import Config from '@/config';
import Main from './components/Main.vue';
import path from 'path';
import { DefaultTheme } from './themes/themes/defaultTheme';
import Route from '@/interfaces/route';
import { ICompetition } from '@cyber-range/cyber-range-api-ctf-competition-client';
import ITheme from '@/interfaces/iTheme';
import SplashScreen from '@components/SplashScreen.vue';
import querystring from 'querystring';
import { Pinia, createPinia, setActivePinia } from 'pinia';
import { useThemeStore } from '@/stores/themeStore';
import { useCompetitionStore } from './stores/competitionStore';
import { createI18n } from 'vue-i18n';
import CrUi from '@cyber-range/cyber-range-lib-ui';
import { CrTheme } from '@cyber-range/cyber-range-lib-ui';
import VueMatomo from 'vue-matomo';
import { isThemeEditorPreview } from '@/themeEditor/isThemeEditorPreview';
import { setupThemeEditorPreview } from '@/themeEditor/setupThemeEditorPreview';


async function getCurrentTheme(pinia:Pinia): Promise<ITheme>
{
	let subdomain = location.hostname.split('.')[0];
	let themeId = querystring.parse(location.search?.substring(1))['themeid'] as string;
	let competition:ICompetition;

	const themeStore = useThemeStore(pinia);

	try
	{
		competition = await useCompetitionStore().fetchCompetition({subdomain: subdomain});
	}
	catch(e)
	{
		console.error(`Failed to retrieve a theme for '${subdomain}'. Use the default theme instead.`, e);
	}

	themeStore.setCurrentTheme(themeId || competition?.settings?.themeId || DefaultTheme.Id);

	return themeStore.getCurrentTheme;
}

async function loadThemedComponents(currentTheme:ITheme, app: App): Promise<void>
{
	// Dynamically search for themed components https://vitejs.dev/guide/features#glob-import
	const componentImports = import.meta.glob('./components/**/*.vue');

	let allComponents = {};

	// Start all imports in parallel
	const loaders = Object.entries(componentImports).map(async ([fileName, loader]) =>
	{
		const componentConfig = await loader();

		let [componentName] = path.basename(fileName, path.extname(fileName)).split('.', 1);

		return { componentName, componentConfig };
	});

	// Wait for imports to resolve
	const results = await Promise.all(loaders);

	for (const { componentName, componentConfig } of results)
	{
		const component = (componentConfig as any).default || componentConfig;
		allComponents[componentName] = component;
	}

	// Globally register selected components
	for(let componentName of Object.keys(allComponents))
	{
		app.component(componentName, allComponents[componentName]);

		// Register page components to use for routes
		if(componentName in Route)
		{
			Route[componentName].component = allComponents[componentName];
		}
	}
}

async function createVueApplication()
{
	if (isThemeEditorPreview())
	{
		setupThemeEditorPreview();
	}

	// Create the base app. lib-ui needs to get set up before setting theme colors in getCurrentTheme() to ensure colors aren't overwritten with undefined.
	const app = createApp(Main)
		.use(pinia)
		.use(CrUi, {
			theme: {
				font: undefined,
				colors: undefined,
				cssVariables: {
					navBarHeight: '4rem',
					dialogWidth: '50%',
					scoreTableRowHeight: '3rem',
					scoreTableHeaderFontSize: '0.75rem',
					scoreTableDataFontSize: '0.875rem'
				}
			} as CrTheme
		})

	// Theme
	let currentTheme = await getCurrentTheme(pinia);
	await loadThemedComponents(currentTheme, app);

	// Create a router.  It is done here so that the Route object 
	// contains the right components based on the current theme.
	let router = (await import ('@/router')).default

	const matomo = {
		host: Config.MATOMO_BASE_URL,
		siteId: Config.MATOMO_SITE_ID,
		router: router,
		enableLinkTracking: true,
		disableCookies: true,
		requireConsent: false,
		trackInitialView: true,
		enableHeartBeatTimer: true,
		trackSiteSearch: true,
	};
	// Remaining plugins
	app
		.use(router)
		.use(i18n)
		.use(VueMatomo.default, matomo)
		
	document.title = Config.APP_TITLE;


	app.config.errorHandler = (err, vm, info) =>
	{
		// Log properties passed to the component if there are any
		const props = vm && (vm as any).$props;
		if (props && Object.keys(props).length > 0)
		{
			console.log('Props passed to component', props);
		}

		// Emit component name and also the lifecycle hook the error occured in if present
		const componentName = vm && (vm as any).$?.type?.__name || 'AnonymousComponent';
		let infoMessage = `Error in component: <${componentName} />\n`;
		if (info)
		{
			infoMessage += `Error occurred during lifecycle hook: ${info}\n`;
		}

		// This puts the additional error information in the Telemetry Timeline
		console.log(infoMessage);
		console.log(err);

		// Track the native JS error
		TrackJS.track(err);
	};

	app.mount('#app')
}

// Pinia - must be the executed first so the router initialized below can access stores.
let pinia = createPinia();
setActivePinia(pinia);

const i18n = createI18n({
	legacy: false,
	missingWarn: false,
	locale: process.env.locale || 'en',
	warnHtmlMessage: false,
	globalInjection: false,
	messages: createMessages(Config.IS_VACR ? 'VACR' : 'USCR'),
	modifiers: {
		quoteWrap: (str) => `"${str}"`
	}
});

// Show a splash screen while loading the real Vue app.
createApp(SplashScreen)
	.use(pinia)
	.use(i18n)
	.use(CrUi, {theme: {} as CrTheme})
	.mount('#app');

Config.load(`${window.location.protocol}//${window.location.hostname}:${window.location.port}/env.json?t=${new Date().getTime()}`).then(()=>createVueApplication());
